import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
  useTransition,
} from "react";
import { ResponsiveTreeMap } from "@nivo/treemap";
import { HeatmapFilters } from "./HeatmapFilters";
import {
  getColor,
  DARK_THEME,
  LIGHT_THEME,
  SCOPE_ID,
  injectSelectStyles,
} from "./HeatmapTheme";
import TreemapNode from "./TreemapNode";
import SectorOverlay, { SectorBounds } from "./SectorOverlay";
import FloatingTooltip, { TooltipState } from "./FloatingTooltip";
import { LoadingData } from "./LoadingData";

export interface StockEntry {
  symbol: string;
  change: number;
  marketCap: number;
  company_name?: string;
  closed?: number;
  volume?: number;
  total_trades?: number;
  open?: number;
  high?: number;
  low?: number;
  sector_id?: number;
  sector_name?: string;
  last_traded_date?: string;
  company_logo?: string;
}

export interface MarketOption {
  id: number | string;
  market_name: string;
}

export interface SectorOption {
  id: number | string;
  name: string;
}

interface HeatmapProps {
  stocks?: StockEntry[];
  height?: number;
  markets?: MarketOption[];
  marketsLoading?: boolean;
  selectedMarket?: number | string;
  onMarketChange?: (value: string | number) => void;
  sectors?: SectorOption[];
  stocksLoading?: boolean;
  selectedSector?: string;
  onSectorChange?: (value: string) => void;
}

interface NodeConfig {
  isDark: boolean;
  hideLogo: boolean;
  groupBySector: boolean;
  onHover: (stock: StockEntry, x: number, y: number) => void;
  onMove: (x: number, y: number) => void;
  onLeave: () => void;
  handleSectorLeaf: (
    sectorId: string,
    sectorName: string,
    x: number,
    y: number,
    w: number,
    h: number,
  ) => void;
}

function makeNodeComponent(configRef: React.MutableRefObject<NodeConfig>) {
  return function NodeComponent(props: any) {
    const { node } = props;
    const { data: nodeData, x, y, width, height } = node;
    const cfg = configRef.current;

    if (cfg.groupBySector && nodeData.sectorId) {
      cfg.handleSectorLeaf(
        nodeData.sectorId,
        nodeData.sectorName ?? "",
        x,
        y,
        width,
        height,
      );
    }

    return (
      <TreemapNode
        {...props}
        isDark={cfg.isDark}
        onHover={cfg.onHover}
        onMove={cfg.onMove}
        onLeave={cfg.onLeave}
        groupBySector={cfg.groupBySector}
        hideLogo={cfg.hideLogo}
      />
    );
  };
}

function computeWeights(
  values: number[],
  power: number,
  minFloor: number,
  maxCellFraction: number,
): number[] {
  const maxRaw = Math.max(...values, 1);
  const rawWeights = values.map((v) => {
    const normalized = v / maxRaw;
    const scaled = Math.pow(normalized, power);
    const floored = minFloor + scaled * (1 - minFloor);
    return Math.round(floored * 10000);
  });
  const totalWeight = rawWeights.reduce((sum, w) => sum + w, 0);
  const maxAllowed = Math.round(totalWeight * maxCellFraction);
  return rawWeights.map((w) => Math.min(w, maxAllowed));
}

function nivoDataEqual(a: any, b: any): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  const aChildren = a.children ?? [];
  const bChildren = b.children ?? [];
  if (aChildren.length !== bChildren.length) return false;
  for (let i = 0; i < aChildren.length; i++) {
    if (aChildren[i].id !== bChildren[i].id) return false;
    if (aChildren[i].value !== bChildren[i].value) return false;
    const ac = aChildren[i].children;
    const bc = bChildren[i].children;
    if (!!ac !== !!bc) return false;
    if (ac && bc && ac.length !== bc.length) return false;
  }
  return true;
}

const Heatmap: React.FC<HeatmapProps> = ({
  stocks,
  height = 580,
  markets,
  marketsLoading,
  selectedMarket,
  onMarketChange,
  sectors,
  stocksLoading,
  selectedSector,
  onSectorChange,
}): React.ReactElement => {
  const [sortBy, setSortBy] = useState<"total_trades" | "market_cap">(
    "total_trades",
  );
  const [isDark, setIsDark] = useState<boolean>(() => {
    return localStorage.getItem("darkMode") === "true";
  });
  const [filterBy, setFilterBy] = useState<
    "gainers" | "losers" | "unchanged" | null
  >(null);
  const [tooltip, setTooltip] = useState<TooltipState | null>(null);
  const [groupBySector, setGroupBySector] = useState<boolean>(true);
  const [sectorBounds, setSectorBounds] = useState<SectorBounds[]>([]);
  const [overlaySize, setOverlaySize] = useState({ w: 0, h: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const [hideLogo, setHideLogo] = useState<boolean>(false);

  const [, startTransition] = useTransition();

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    injectSelectStyles(isDark, SCOPE_ID);
  }, [isDark]);

  const theme = isDark ? DARK_THEME : LIGHT_THEME;
  const dropdownClass = `heatmap-select-dropdown-${SCOPE_ID}`;

  const handleHover = useCallback(
    (stock: StockEntry, x: number, y: number) => setTooltip({ stock, x, y }),
    [],
  );
  const handleMove = useCallback(
    (x: number, y: number) =>
      setTooltip((prev) => (prev ? { ...prev, x, y } : prev)),
    [],
  );
  const handleLeave = useCallback(() => setTooltip(null), []);

  const sectorAccRef = useRef<
    Map<
      string,
      { name: string; minX: number; minY: number; maxX: number; maxY: number }
    >
  >(new Map());
  const flushScheduledRef = useRef(false);

  const handleSectorLeaf = useCallback(
    (
      sectorId: string,
      sectorName: string,
      x: number,
      y: number,
      w: number,
      h: number,
    ) => {
      const acc = sectorAccRef.current;
      const existing = acc.get(sectorId);
      if (!existing) {
        acc.set(sectorId, {
          name: sectorName,
          minX: x,
          minY: y,
          maxX: x + w,
          maxY: y + h,
        });
      } else {
        existing.minX = Math.min(existing.minX, x);
        existing.minY = Math.min(existing.minY, y);
        existing.maxX = Math.max(existing.maxX, x + w);
        existing.maxY = Math.max(existing.maxY, y + h);
      }

      if (!flushScheduledRef.current) {
        flushScheduledRef.current = true;
        requestAnimationFrame(() => {
          flushScheduledRef.current = false;
          const bounds: SectorBounds[] = [];
          sectorAccRef.current.forEach((v, id) => {
            bounds.push({
              sectorId: id,
              sectorName: v.name,
              x: v.minX,
              y: v.minY,
              width: v.maxX - v.minX,
              height: v.maxY - v.minY,
            });
          });

          startTransition(() => {
            setSectorBounds(bounds);
            if (containerRef.current) {
              const svgEl = containerRef.current.querySelector("svg");
              if (svgEl) {
                const r = svgEl.getBoundingClientRect();
                setOverlaySize({ w: r.width, h: r.height });
              }
            }
          });
        });
      }
    },
    [startTransition],
  );

  const configRef = useRef<NodeConfig>({
    isDark,
    groupBySector,
    onHover: handleHover,
    onMove: handleMove,
    onLeave: handleLeave,
    handleSectorLeaf,
    hideLogo,
  });
  configRef.current.isDark = isDark;
  configRef.current.groupBySector = groupBySector;
  configRef.current.onHover = handleHover;
  configRef.current.onMove = handleMove;
  configRef.current.onLeave = handleLeave;
  configRef.current.handleSectorLeaf = handleSectorLeaf;
  configRef.current.hideLogo = hideLogo;

  const NodeComponent = useMemo(() => makeNodeComponent(configRef), []);

  useEffect(() => {
    sectorAccRef.current = new Map();
    setSectorBounds([]);
  }, [stocks, sortBy, filterBy, groupBySector]);

  const data = useMemo(() => {
    let filtered = stocks ?? [];
    if (filterBy === "gainers") filtered = filtered.filter((s) => s.change > 0);
    else if (filterBy === "losers")
      filtered = filtered.filter((s) => s.change < 0);
    else if (filterBy === "unchanged")
      filtered = filtered.filter((s) => s.change === 0);

    return [...filtered].sort((a, b) =>
      sortBy === "market_cap"
        ? (b.marketCap ?? 0) - (a.marketCap ?? 0)
        : (b.total_trades ?? 0) - (a.total_trades ?? 0),
    );
  }, [stocks, sortBy, filterBy]);

  const nivoDataFlatRaw = useMemo(() => {
    if (!data.length) return { id: "root", children: [] };
    const values = data.map((s) =>
      sortBy === "market_cap"
        ? s.marketCap && s.marketCap > 0
          ? s.marketCap
          : 1
        : s.total_trades && s.total_trades > 0
          ? s.total_trades
          : 1,
    );
    const weights = computeWeights(values, 1, 0.001, 0.15);
    return {
      id: "root",
      children: data.map((s, i) => ({
        id: `${s.symbol}_${i}`,
        value: weights[i],
        stock: s,
      })),
    };
  }, [data, sortBy]);

  const nivoDataGroupedRaw = useMemo(() => {
    if (!data.length) return { id: "root", children: [] };

    const sectorMap = new Map<
      string,
      { sectorName: string; stocks: StockEntry[] }
    >();
    data.forEach((s) => {
      const key = s.sector_id != null ? String(s.sector_id) : "unknown";
      const name = s.sector_name ?? "Other";
      if (!sectorMap.has(key))
        sectorMap.set(key, { sectorName: name, stocks: [] });
      sectorMap.get(key)!.stocks.push(s);
    });

    const sectorChildren = Array.from(sectorMap.entries()).map(
      ([sectorKey, { sectorName, stocks: ss }]) => {
        const values = ss.map((s) =>
          sortBy === "market_cap"
            ? s.marketCap && s.marketCap > 0
              ? s.marketCap
              : 1
            : s.total_trades && s.total_trades > 0
              ? s.total_trades
              : 1,
        );
        const weights = computeWeights(values, 1, 0.001, 0.25);
        return {
          id: `sector_${sectorKey}`,
          sectorName,
          children: ss.map((s, i) => ({
            id: `${s.symbol}_${sectorKey}_${i}`,
            value: weights[i],
            stock: s,
            sectorId: sectorKey,
            sectorName,
          })),
        };
      },
    );

    sectorChildren.sort((a, b) => {
      const sum = (arr: typeof a.children) =>
        arr.reduce((acc, c) => acc + c.value, 0);
      return sum(b.children) - sum(a.children);
    });

    return { id: "root", children: sectorChildren };
  }, [data, sortBy]);

  const stableNivoDataRef = useRef<any>(null);
  const rawNivoData = groupBySector ? nivoDataGroupedRaw : nivoDataFlatRaw;

  if (!nivoDataEqual(stableNivoDataRef.current, rawNivoData)) {
    stableNivoDataRef.current = rawNivoData;
  }
  const nivoData = stableNivoDataRef.current;

  const gainers = (stocks ?? []).filter((s) => s.change > 0).length;
  const losers = (stocks ?? []).filter((s) => s.change < 0).length;
  const unchanged = (stocks ?? []).filter((s) => s.change === 0).length;
  const totalSecurities = (stocks ?? []).length;

  return (
    <div
      data-heatmap-scope={SCOPE_ID}
      style={{
        background: theme.bg,
        borderRadius: "12px",
        border: `1px solid ${theme.border}`,
        padding: "16px",
        width: "100%",
        marginBottom: "24px",
        boxSizing: "border-box",
        transition: "background 0.3s, border-color 0.3s, box-shadow 0.3s",
        // Panel-tier glass — this is the one big surrounding container on
        // the page, same role as every other page's single main content
        // panel. theme.bg's own gradient stops are now ~92% opaque
        // (were fully solid) so this blur has something to actually show
        // through, without diluting the heat-tile grid's own readability
        // (still nowhere near as translucent as .vi-glass-card's ~4-5%).
        backdropFilter: "var(--vi-glass-blur-panel)",
        WebkitBackdropFilter: "var(--vi-glass-blur-panel)",
        // GLOWY (on request), matching every other themed page's main
        // panel: an accent-tinted glow layered into the shadow instead
        // of the flat border-only edge this had before. var(--vi-accent)
        // already resolves correctly for whichever dark/light mode and
        // blue/teal palette is active, so one rule covers both themes.
        boxShadow: `0 1px 2px rgba(15, 23, 42, 0.05), 0 20px 44px rgba(15, 23, 42, ${
          isDark ? 0.35 : 0.1
        }), 0 0 32px color-mix(in srgb, var(--vi-accent) 16%, transparent)`,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "8px",
        }}
      >
        <div>
          <HeatmapFilters
            markets={markets}
            marketsLoading={marketsLoading}
            selectedMarket={selectedMarket}
            onMarketChange={onMarketChange}
            sectors={sectors}
            stocksLoading={stocksLoading}
            selectedSector={selectedSector}
            onSectorChange={onSectorChange}
            selectLabelColor={theme.selectLabelColor}
            dropdownClass={dropdownClass}
            legendText={theme.legendText}
            sortBy={sortBy}
            onSortChange={setSortBy}
            groupBySector={groupBySector}
            onGroupBySectorChange={(value) => {
              setGroupBySector(value);
              if (value && onSectorChange) {
                onSectorChange("all");
              }
            }}
            hideLogo={hideLogo}
            onHideLogoChange={setHideLogo}
          />
        </div>

        <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
          {[
            { label: `▲ ${gainers} Gainers`, color: "#16a34a", key: "gainers" },
            {
              label: `● ${unchanged} Unchanged`,
              color: theme.unchangedPill,
              key: "unchanged",
            },
            { label: `▼ ${losers} Losers`, color: "#b91c1c", key: "losers" },
            {
              label: `◆ ${totalSecurities} Securities`,
              color: theme.labelColor,
              key: "all",
            },
          ].map(({ label, color, key }) => {
            const isActive = filterBy === key;
            return (
              <span
                key={key}
                onClick={() => {
                  if (key === "all") setFilterBy(null);
                  else setFilterBy(isActive ? null : (key as any));
                }}
                style={{
                  background: isActive ? color + "44" : color + "22",
                  border: `1px solid ${isActive ? color : color + "55"}`,
                  color,
                  borderRadius: "20px",
                  padding: "2px 10px",
                  fontSize: "11px",
                  fontFamily: "'DM Mono', monospace",
                  fontWeight: 600,
                  cursor: "pointer",
                  transform: isActive ? "scale(1.05)" : "scale(1)",
                  transition: "all 0.15s ease",
                  userSelect: "none",
                }}
              >
                {label}
              </span>
            );
          })}
        </div>
      </div>

      <div
        ref={containerRef}
        style={{
          height,
          width: "100%",
          marginTop: "8px",
          position: "relative",
        }}
      >
        {data.length === 0 ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: theme.labelColor,
              fontFamily: "'DM Mono', monospace",
              fontSize: 13,
            }}
          >
            <LoadingData />
          </div>
        ) : (
          <>
            <ResponsiveTreeMap
              data={nivoData}
              identity="id"
              value="value"
              tile="squarify"
              innerPadding={2}
              outerPadding={groupBySector ? 6 : 2}
              enableLabel={false}
              nodeComponent={NodeComponent}
              tooltip={() => null}
              animate={false}
              leavesOnly={true}
              colors={(node: any) =>
                node.data?.stock
                  ? getColor(node.data.stock?.change ?? 0)
                  : "transparent"
              }
            />

            {groupBySector && sectorBounds.length > 0 && (
              <SectorOverlay
                sectorBounds={sectorBounds}
                isDark={isDark}
                width={
                  overlaySize.w || containerRef.current?.clientWidth || 800
                }
                height={overlaySize.h || height}
              />
            )}
          </>
        )}
      </div>

      <FloatingTooltip tooltip={tooltip} theme={theme} isDark={isDark} />
    </div>
  );
};

export default Heatmap;
